import React from "react";
import {AlertOutlined, CheckOutlined, WarningFilled} from "@ant-design/icons";
import {Text, TextAs, TextSizes} from "@clockout-dev/design-elements";
import SectionCard from "../../common/sectionCard/SectionCard";
import SilentBadge from "./SilentBadge";
import {FRAUD_SIGNAL_META} from "../uploads-simulator.constants";
import styles from "../styles/results.module.scss";

/**
 * The full fraud-signal vocabulary, each rendered as triggered or clear.
 * Triggered = present on the selected verdict OR flagged at run level in
 * `silent_signals` (aggregate list + the boolean flags). Signals that
 * production silences carry the gray `SilentBadge`. Would-be fraud tickets
 * are listed underneath — in production these are the ONLY trace.
 *
 * @param {Object} props
 * @param {Object} [props.verdict] — selected entry from report `verdicts`
 * @param {Object} [props.silentSignals] — report `silent_signals`
 */
function FraudSignalsCard({verdict, silentSignals}) {
    const triggered = new Set([...(Array.isArray(verdict?.fraud_signals) ? verdict.fraud_signals : []), ...(Array.isArray(silentSignals?.all_fraud_signals) ? silentSignals.all_fraud_signals : [])]);
    if (silentSignals?.accepted_after_repeated_rejections) triggered.add("accepted_after_repeated_rejections");
    if (silentSignals?.iterative_resubmission) triggered.add("iterative_resubmission");
    if (silentSignals?.fi_owned_routing_match) triggered.add("fi_owned_routing_match");

    const knownKeys = Object.keys(FRAUD_SIGNAL_META);
    const unknownTriggered = [...triggered].filter(signal => !knownKeys.includes(signal));
    const tickets = Array.isArray(silentSignals?.fraud_tickets) ? silentSignals.fraud_tickets : [];

    return (
        <SectionCard icon={<AlertOutlined />} title={`Fraud Signals (${triggered.size} triggered)`}>
            <div className={styles.signalList}>
                {knownKeys.map(key => {
                    const meta = FRAUD_SIGNAL_META[key];
                    const isTriggered = triggered.has(key);
                    return (
                        <div key={key} className={`${styles.signalRow} ${isTriggered ? styles.signalTriggered : styles.signalClear}`}>
                            <span className={styles.signalIcon}>{isTriggered ? <WarningFilled /> : <CheckOutlined />}</span>
                            <div className={styles.signalBody}>
                                <div className={styles.signalHeadline}>
                                    <Text as={TextAs.SPAN} size={TextSizes.SMALL} className={styles.signalLabel}>
                                        {meta.label}
                                    </Text>
                                    {meta.silentInProduction ? <SilentBadge /> : null}
                                </div>
                                <Text as={TextAs.P} size={TextSizes.EXTRA_SMALL} className={styles.signalDescription}>
                                    {meta.description}
                                </Text>
                            </div>
                        </div>
                    );
                })}
                {unknownTriggered.map(signal => (
                    <div key={signal} className={`${styles.signalRow} ${styles.signalTriggered}`}>
                        <span className={styles.signalIcon}>
                            <WarningFilled />
                        </span>
                        <div className={styles.signalBody}>
                            <Text as={TextAs.SPAN} size={TextSizes.SMALL} className={styles.signalLabel}>
                                {signal}
                            </Text>
                        </div>
                    </div>
                ))}
            </div>

            {tickets.length ? (
                <div className={styles.ticketBlock}>
                    <Text as={TextAs.SPAN} size={TextSizes.EXTRA_SMALL} className={styles.ticketTitle}>
                        Would-be fraud tickets ({tickets.length})
                    </Text>
                    {tickets.map((ticket, index) => (
                        <Text key={ticket?.id ?? index} as={TextAs.P} size={TextSizes.EXTRA_SMALL} className={styles.ticketRow}>
                            {ticket?.message || ticket?.params?.reason || "Fraud ticket"}
                        </Text>
                    ))}
                </div>
            ) : null}
        </SectionCard>
    );
}

export default FraudSignalsCard;
